import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, FormControl, IconButton, InputLabel, Typography } from '@mui/material';
import React from 'react';
import SearchableSelect from './SearchableSelect';

export interface TranscriptionNavOption {
  value: string;
  label: string;
}

interface TranscriptionNavProps {
  value: string;
  options: TranscriptionNavOption[];
  disabled: boolean;
  onChange: (id: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  iconButtons?: boolean;
  labelPosition?: number | null;
  labelCount?: number;
}

const navButtonSx = { whiteSpace: 'nowrap', flexShrink: 0 };

const iconNavButtonSx = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  borderRadius: 1,
  '&:hover': {
    bgcolor: 'primary.dark',
  },
};

const TranscriptionNav: React.FC<TranscriptionNavProps> = ({
  value,
  options,
  disabled,
  onChange,
  onPrevious,
  onNext,
  iconButtons = false,
  labelPosition,
  labelCount,
}) => {
  const navDisabled = disabled || options.length === 0;
  const labelCopy =
    labelCount === undefined
      ? null
      : labelCount === 0
        ? 'No labels'
        : `Label ${labelPosition ?? '–'} of ${labelCount}`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
        <InputLabel>Transcription</InputLabel>
        <SearchableSelect
          value={value}
          label="Transcription"
          onChange={e => onChange(e.target.value as string)}
          disabled={disabled}
          options={options}
        />
      </FormControl>
      {labelCopy && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {labelCopy}
        </Typography>
      )}
      {iconButtons ? (
        <>
          <IconButton
            aria-label="Previous"
            onClick={onPrevious}
            disabled={navDisabled}
            size="small"
            sx={iconNavButtonSx}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            aria-label="Next"
            onClick={onNext}
            disabled={navDisabled}
            size="small"
            sx={iconNavButtonSx}
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      ) : (
        <>
          <Button
            variant="outlined"
            size="small"
            sx={navButtonSx}
            onClick={onPrevious}
            disabled={navDisabled}
          >
            Previous (←)
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={navButtonSx}
            onClick={onNext}
            disabled={navDisabled}
          >
            Next (→)
          </Button>
        </>
      )}
    </Box>
  );
};

export default TranscriptionNav;
